import { useEffect, useState } from 'react';
import { Icon } from '../components/Icon';
import { AuditSchedules } from '../components/AuditSchedules';
import { NoticeEditor, type PlaceholderChip } from '../components/NoticeEditor';
import { api, ApiError } from '../lib/api';
import { SectionHeading, useSectionOpen } from '../components/SectionToggle';

interface ConfigKey {
  key: string;
  label: string;
  type: 'number' | 'string' | 'string_array' | 'boolean';
  desc: string;
  default: unknown;
  value: unknown;
  min?: number;
  max?: number;
}

export function Configuration() {
  const [settingsOpen, toggleSettings] = useSectionOpen('settings');
  const [keys, setKeys] = useState<ConfigKey[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, unknown>>({});

  const load = () => {
    api<{ keys: ConfigKey[] }>('/api/admin/config')
      .then((d) => {
        setKeys(d.keys);
        const initial: Record<string, unknown> = {};
        for (const k of d.keys) initial[k.key] = k.value;
        setDrafts(initial);
      })
      .catch((e) => setError(e instanceof ApiError && e.status === 403
        ? 'Admin role required to view configuration.'
        : (e instanceof Error ? e.message : 'load failed')));
  };
  useEffect(load, []);

  const save = async (k: ConfigKey) => {
    setSavingKey(k.key);
    setError(null);
    setSavedKey(null);
    try {
      await api(`/api/admin/config/${encodeURIComponent(k.key)}`, {
        method: 'PUT',
        body: { value: drafts[k.key] },
      });
      setSavedKey(k.key);
      setTimeout(() => setSavedKey(null), 2000);
      load();
    } catch (e) {
      const msg = e instanceof ApiError && (e.body as { error?: string })?.error
        ? (e.body as { error: string }).error
        : (e instanceof Error ? e.message : 'save failed');
      setError(`${k.label}: ${msg}`);
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="scroll-y" style={{ flex: 1, padding: 24 }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Admin</div>
        <SectionHeading open={settingsOpen} onToggle={toggleSettings} level={1}>Configuration</SectionHeading>
        {settingsOpen && (
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
            Audit-engine knobs. Changes apply on the next audit run; in-flight runs use the snapshot at trigger time.
          </div>
        )}
      </div>
      {settingsOpen && <>
      {error && <div className="card card-pad c-warn" style={{ fontSize: 13, marginBottom: 12 }}>{error}</div>}
      {!keys && !error && <div className="card card-pad muted" style={{ fontSize: 13 }}>Loading…</div>}
      {keys && (
        <div className="card" style={{ padding: 0 }}>
          {keys.map((k, i) => {
            const dirty = JSON.stringify(drafts[k.key]) !== JSON.stringify(k.value);
            return (
              <div key={k.key} style={{ padding: 18, borderBottom: i < keys.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{k.label}</div>
                    <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{k.desc}</div>
                    <div className="faint mono" style={{ fontSize: 11, marginTop: 4 }}>
                      {k.key} · default {JSON.stringify(k.default)}
                    </div>
                  </div>
                  <div style={{ minWidth: 280, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Editor k={k} value={drafts[k.key]} onChange={(v) => setDrafts((d) => ({ ...d, [k.key]: v }))} />
                    <button
                      className="btn btn-sm"
                      disabled={!dirty || savingKey === k.key}
                      onClick={() => save(k)}
                      style={{ background: dirty ? 'var(--accent)' : 'var(--surface-2)', color: dirty ? '#fff' : 'var(--text-muted)' }}
                    >
                      {savingKey === k.key ? 'Saving…' : savedKey === k.key ? 'Saved ✓' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="muted" style={{ fontSize: 11.5, marginTop: 16 }}>
        <Icon name="alert" size={11} /> Changes are logged to admin_actions with the previous and new value.
      </div>
      </>}

      <AuditSchedules />
      <NotificationTemplates />
    </div>
  );
}

// ---- Encroachment notice templates ----
// The two franchisee-facing notice templates (offending servicer + territory
// owner). Wording is iterated with David/Bettina/Matt directly here — saves
// apply to the NEXT drafted notice, no deploy. Already-drafted notices keep
// their text (each draft is a frozen copy, editable per-case on the
// Notifications tab).

interface Template {
  id: number;
  slug: string;
  subject_template: string;
  body_template: string;
  description: string | null;
  updated_at: string;
}

// Sample values for the live preview so editors see a realistic notice
// instead of raw {{placeholders}}. Highlighted in the preview so it's obvious
// which parts the tool fills in per case.
const SAMPLE_VARS: Record<string, string> = {
  case_id: 'CASE-2084',
  servicing_af: 'AF-268',
  servicing_name: 'AF-268',
  owner_af: 'AF-227',
  owner_name: 'AF-227',
  zip: '61038',
  visit_count: '14',
  fbc_name: 'Greg Coopman',
  client_name: 'Palmer, Violet',
  client_initials: 'V.P.',
  servicing_owner_full_name: 'Jane Smith and John Smith',
  servicing_owner_first_name: 'Jane and John',
  territory_owner_full_name: 'Robert Jones',
  territory_owner_first_name: 'Robert',
  client_id: '48213',
  notice_date: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
  response_form_link: '<a href="#" onclick="return false" style="text-decoration:underline">AFI Service Permission Contract</a>',
  notice_date_plus_2_business_days: sampleBusinessDays(2),
  notice_date_plus_7_business_days: sampleBusinessDays(7),
};

// Preview only: today + N business days (Mon–Fri), like the send does.
function sampleBusinessDays(n: number): string {
  const d = new Date();
  let added = 0;
  while (added < n) { d.setDate(d.getDate() + 1); if (d.getDay() !== 0 && d.getDay() !== 6) added++; }
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

const CHIPS: PlaceholderChip[] = [
  { token: '{{servicing_af}}', label: 'Servicing AF#' },
  { token: '{{owner_af}}', label: 'Territory owner AF#' },
  { token: '{{zip}}', label: 'Zip code' },
  { token: '{{visit_count}}', label: 'Visit count' },
  { token: '{{fbc_name}}', label: 'FBC name' },
  { token: '{{case_id}}', label: 'Case #' },
  { token: '{{client_name}}', label: 'Client name' },
  { token: '{{client_initials}}', label: 'Client initials' },
  // Bettina, 2026-10-22. Owner names come from Zoho (primary owner); the
  // notice date is filled in when the notice is sent.
  { token: '{{servicing_owner_full_name}}', label: 'Servicing owner(s) full name' },
  { token: '{{servicing_owner_first_name}}', label: 'Servicing owner(s) first name' },
  { token: '{{territory_owner_full_name}}', label: 'Territory owner(s) full name' },
  { token: '{{territory_owner_first_name}}', label: 'Territory owner(s) first name' },
  { token: '{{client_id}}', label: 'Client ID# (AxisCare)' },
  { token: '{{notice_date}}', label: 'Notice date (date sent)' },
  { token: '{{notice_date_plus_2_business_days}}', label: 'Notice date + 2 business days' },
  { token: '{{notice_date_plus_7_business_days}}', label: 'Notice date + 7 business days' },
  { token: '{{response_form_link}}', label: 'Webform link (AFI Service Permission Contract)' },
  // Decision 0006: a notice can list several cases. Servicing notice: by
  // territory owner, Client ID · initials · zip. Territory-owner notice: by
  // servicing franchisee, client count and zip(s) only.
  { token: '{{case_list}}', label: 'List of cases in this notice' },
  { token: '{{case_count}}', label: 'Number of cases in this notice' },
];

const TEMPLATE_LABEL: Record<string, string> = {
  encroachment_notice_offending: 'Servicing notice (to the servicing franchisee)',
  encroachment_notice_neighbor: 'Territory-owner notice (to the territory owner)',
};

// {{case_list}} preview per template (decision 0006), matching what is sent:
// the servicing notice is a table (Territory · Client ID · initials · zip);
// the territory-owner notice lists servicing franchisees, counts and zips.
const SAMPLE_CASE_LIST: Record<string, string> = {
  encroachment_notice_offending: `<table style="border-collapse:collapse;font-size:13px"><tr><th style="border:1px solid #ddd;padding:3px 8px">Territory</th><th style="border:1px solid #ddd;padding:3px 8px">Client ID</th><th style="border:1px solid #ddd;padding:3px 8px">Client Initials</th><th style="border:1px solid #ddd;padding:3px 8px">Client Zip</th></tr>
    <tr><td style="border:1px solid #ddd;padding:3px 8px">AF-227 Robert Jones</td><td style="border:1px solid #ddd;padding:3px 8px">48213</td><td style="border:1px solid #ddd;padding:3px 8px">V.P.</td><td style="border:1px solid #ddd;padding:3px 8px">61038</td></tr>
    <tr><td style="border:1px solid #ddd;padding:3px 8px">AF-291 Maria Lopez</td><td style="border:1px solid #ddd;padding:3px 8px">48377</td><td style="border:1px solid #ddd;padding:3px 8px">J.D.</td><td style="border:1px solid #ddd;padding:3px 8px">61039</td></tr></table>`,
  encroachment_notice_neighbor: `<p style="margin:4px 0"><strong>2</strong> client(s) serviced by <strong>AF-268, Jane Smith and John Smith,</strong> in zip codes <strong>61038, 61039</strong></p><p style="margin:4px 0"><strong>1</strong> client(s) serviced by <strong>AF-291, Maria Lopez,</strong> in zip code <strong>61040</strong></p>`,
};

function renderSample(tpl: string, slug?: string): string {
  return tpl.replace(/\{\{(\w+)\}\}/g, (_, k: string) => {
    const v = k === 'case_list' ? (SAMPLE_CASE_LIST[slug ?? ''] ?? SAMPLE_CASE_LIST.encroachment_notice_offending)
      : k === 'case_count' ? '2' : (SAMPLE_VARS[k] ?? '—');
    return `<span style="background:var(--warn-soft);border-radius:3px;padding:0 3px" title="{{${k}}} — filled per notice">${v}</span>`;
  });
}

function NotificationTemplates() {
  const [open, toggle] = useSectionOpen('templates');
  const [templates, setTemplates] = useState<Template[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<{ templates: Template[] }>('/api/notifications/templates')
      .then((d) => setTemplates(d.templates))
      .catch((e) => setError(e instanceof ApiError && e.status === 403
        ? 'Admin role required to edit notice templates.'
        : (e instanceof Error ? e.message : 'load failed')));
  }, []);

  return (
    <div style={{ marginTop: 32 }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Admin</div>
        <SectionHeading open={open} onToggle={toggle}>Encroachment notice templates</SectionHeading>
        {open && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
          Edit on the left, see the finished email on the right (highlighted parts are filled in
          automatically per case — use the Insert buttons to add them). Saving applies to the{' '}
          <b>next</b> drafted notice; already-drafted ones keep their text.
          <div className="c-warn" style={{ marginTop: 6 }}>
            Client name goes to the servicing franchisee only. The territory owner's copy must not
            name the client — use <code className="mono">{'{{client_initials}}'}</code> or leave it out.
          </div>
        </div>}
      </div>
      {open && <>
      {error && <div className="card card-pad c-warn" style={{ fontSize: 13 }}>{error}</div>}
      {!templates && !error && <div className="card card-pad muted" style={{ fontSize: 13 }}>Loading…</div>}
      {templates?.map((t) => <TemplateCard key={t.slug} t={t} />)}
      </>}
    </div>
  );
}

function TemplateCard({ t }: { t: Template }) {
  const [subject, setSubject] = useState(t.subject_template);
  const [body, setBody] = useState(t.body_template);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const dirty = subject !== t.subject_template || body !== t.body_template;

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      await api(`/api/notifications/templates/${encodeURIComponent(t.slug)}`, {
        method: 'PUT',
        body: { subject_template: subject, body_template: body },
      });
      t.subject_template = subject;
      t.body_template = body;
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'save failed');
    } finally { setSaving(false); }
  };

  const reset = () => { setSubject(t.subject_template); setBody(t.body_template); };

  return (
    <div className="card" style={{ padding: 0, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px', borderBottom: '1px solid var(--border)' }}>
        <Icon name="bell" size={14} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{TEMPLATE_LABEL[t.slug] ?? t.slug}</div>
          {t.description && <div className="muted" style={{ fontSize: 11.5, marginTop: 1 }}>{t.description}</div>}
        </div>
        {dirty && (
          <button className="btn btn-sm btn-ghost" onClick={reset} disabled={saving}>Undo changes</button>
        )}
        <button
          className="btn btn-sm"
          disabled={!dirty || saving}
          onClick={save}
          style={{ background: dirty ? 'var(--accent)' : 'var(--surface-2)', color: dirty ? '#fff' : 'var(--text-muted)' }}
        >
          {saving ? 'Saving…' : saved ? 'Saved ✓' : dirty ? 'Save changes' : 'Saved'}
        </button>
      </div>
      {err && <div className="c-danger" style={{ padding: '8px 18px', fontSize: 12, borderBottom: '1px solid var(--border)' }}>{err}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 0 }}>
        {/* Left: edit */}
        <div style={{ padding: '14px 18px', borderRight: '1px solid var(--border)' }}>
          <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
            Edit
          </div>
          <input
            className="input"
            style={{ fontSize: 12.5, width: '100%', marginBottom: 10 }}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject line"
            title="Subject line — placeholders like {{zip}} are filled per case"
          />
          <NoticeEditor html={body} onChange={setBody} chips={CHIPS} minHeight={240} />
        </div>
        {/* Right: live preview with sample values */}
        <div style={{ padding: '14px 18px', background: 'var(--bg)' }}>
          <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
            Preview <span className="faint" style={{ textTransform: 'none', letterSpacing: 0 }}>— what the franchisee sees (highlighted = filled per case)</span>
          </div>
          <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid var(--border)' }}
            dangerouslySetInnerHTML={{ __html: renderSample(escapeSubject(subject)) }} />
          <div
            style={{ fontSize: 13, lineHeight: 1.55 }}
            dangerouslySetInnerHTML={{ __html: renderSample(body, t.slug) }}
          />
        </div>
      </div>
    </div>
  );
}

function escapeSubject(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function Editor({ k, value, onChange }: { k: ConfigKey; value: unknown; onChange: (v: unknown) => void }) {
  if (k.type === 'number') {
    return (
      <input
        type="number" className="input" style={{ width: 120, fontSize: 13 }}
        min={k.min} max={k.max} step={k.min != null && k.min < 1 ? 0.05 : 1}
        value={typeof value === 'number' ? value : Number(value ?? 0)}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    );
  }
  if (k.type === 'boolean') {
    return (
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
        <input type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} />
        {value === true ? 'On' : 'Off'}
      </label>
    );
  }
  if (k.type === 'string_array') {
    return <StatePicker value={Array.isArray(value) ? (value as string[]) : []} onChange={onChange} />;
  }
  return (
    <input
      type="text" className="input" style={{ width: k.key.includes('url') ? 'min(560px, 100%)' : 220, fontSize: 12.5 }}
      value={String(value ?? '')} onChange={(e) => onChange(e.target.value)}
    />
  );
}

// Toggle-button state picker, matching the Run audit page. Pulls the full
// list of states-with-franchisees from /api/audit-jobs/franchisee-states so
// admins click rather than free-type comma-separated codes.
function StatePicker({ value, onChange }: { value: string[]; onChange: (v: unknown) => void }) {
  const [available, setAvailable] = useState<{ state: string; site_count: number }[] | null>(null);
  useEffect(() => {
    api<{ states: { state: string; site_count: number }[] }>('/api/audit-jobs/franchisee-states')
      .then((d) => setAvailable(d.states))
      .catch(() => setAvailable([]));
  }, []);

  const toggle = (s: string) => {
    const next = value.includes(s) ? value.filter((x) => x !== s) : [...value, s];
    onChange(next);
  };

  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: 480 }}>
      {!available && <span className="muted" style={{ fontSize: 12 }}>Loading…</span>}
      {available?.map((s) => {
        const on = value.includes(s.state);
        return (
          <button
            key={s.state}
            className="btn btn-sm"
            style={{
              background: on ? 'var(--accent)' : 'var(--surface-2)',
              color: on ? '#fff' : 'var(--text)',
              borderColor: on ? 'var(--accent)' : 'var(--border)',
            }}
            onClick={() => toggle(s.state)}
            title={`${s.site_count} sites in ${s.state}`}
          >
            {s.state}
          </button>
        );
      })}
    </div>
  );
}
